import * as React from 'react';
import { useEffect } from 'react';

import styled from 'styled-components';

import { COLOR, FONT_SIZE, SPACING, THEME } from '../../packages/constants';
import type { Theme } from '../../packages/constants';
import { Button } from '../button';

export interface ModalProps {
    children: React.ReactNode;
    /**
     * Modal buttons
     */
    buttons?: React.ReactNode;
    /**
     * Button group arrangement type
     */
    buttonGroupType?: ButtonGroupType;
    theme?: Theme | string;
    /**
     * Title of modal
     */
    title?: string;
    /**
     * Cancel button text
     */
    cancelText?: string;
    /**
     * Primary button text
     */
    primaryText?: string;
    /**
     * Function to handle cancel button
     */
    handleCancelButton?: (e: any) => void;
    /**
     * Function to handle primary button
     */
    handlePrimaryButton?: (e: any) => void;
    /**
     * Width of modal (420px)
     */
    width?: string;
    /**
     * Height of modal
     */
    height?: string;
    /**
     * Focus on a particular button
     */
    focusOnButton?: string;
    themeProvider?: {};
}

export enum ButtonGroupType {
    Left = 'LEFT',
    Right = 'RIGHT',
}

export interface ButtonGroupProps {
    buttonGroupType?: ButtonGroupType;
}

interface ModalTheme {
    theme?: {
        main: string;
        mainText: string;
        mainBorder: string;
        spinnerColor: 'black' | 'white';
        hover?: { main?: string; mainBorder?: string; mainText?: string };
    };
}

const Overlay = styled.div`
    background-color: ${COLOR.MODAL.MODAL_OVERLAY};
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
`;

const StyledModal = styled.div`
    border-radius: 4px;
    width: ${(props: ModalProps) => (props.width ? props.width : '900px')};
    height: ${(props: ModalProps) => (props.height ? props.height : '70vh')};
    display: flex;
    flex-direction: column;
`;

const StyledModalTitleBar = styled.div<ModalTheme>`
    background-color: ${(props: ModalTheme) => (props.theme ? props.theme.main : COLOR.BRAND.REPORTING)};
    color: ${COLOR.PRIMARY.WHITE};
    padding: 12px;
    border-top-left-radius: 4px;
    border-top-right-radius: 4px;
    font-size: ${FONT_SIZE.MEDIUM};
    font-weight: 700;
`;

const StyledModalContent = styled.div`
    background-color: ${COLOR.PRIMARY.WHITE};
    height: calc(100% - 42px - 65px);
`; // 42px = titlebar, 65px = button bar

const StyledButton = styled(Button)``;

const StyledButtonGroup = styled.div`
    display: flex;
    justify-content: ${(props: ButtonGroupProps) =>
        props.buttonGroupType === ButtonGroupType.Right ? 'flex-start' : ''};
    flex-direction: ${(props: ButtonGroupProps) =>
        props.buttonGroupType === ButtonGroupType.Right ? 'row-reverse' : ''};
    background-color: ${COLOR.MAIN.BUTTON_GROUP_BG};
    border-top: 1px solid ${COLOR.MAIN.BUTTON_GROUP_BORDER};
    border-bottom-left-radius: 4px;
    border-bottom-right-radius: 4px;
    padding: 16px;

    ${StyledButton} {
        margin-left: ${SPACING.X_LARGE};
        margin-right: ${SPACING.X_LARGE};

        &:first-child {
            margin-left: 0;
            margin-right: 0;
        }
    }
`;

export const Modal = React.forwardRef<HTMLDivElement, ModalProps>(
    (
        {
            children,
            buttons = null,
            buttonGroupType = ButtonGroupType.Left,
            title,
            cancelText,
            primaryText,
            handleCancelButton,
            handlePrimaryButton,
            width,
            focusOnButton,
            ...props
        },
        ref,
    ) => {
        const cancelButtonRef = React.useRef<HTMLButtonElement>(null);
        const primaryButtonRef = React.useRef<HTMLButtonElement>(null);

        useEffect(() => {
            if (focusOnButton === 'cancel' && cancelButtonRef.current) cancelButtonRef.current.focus();
            if (focusOnButton === 'primary' && primaryButtonRef.current) primaryButtonRef.current.focus();
        });

        return (
            <StyledModal ref={ref} width={width} buttonGroupType={buttonGroupType} {...props}>
                <StyledModalTitleBar>{title}</StyledModalTitleBar>
                <StyledModalContent>{children}</StyledModalContent>
                {buttons ? (
                    buttons
                ) : (
                    <StyledButtonGroup buttonGroupType={buttonGroupType}>
                        <StyledButton
                            dataId={'modal__button--add'}
                            onClick={handlePrimaryButton}
                            ref={primaryButtonRef}
                        >
                            {primaryText || 'Add'}
                        </StyledButton>
                        <StyledButton
                            theme={THEME.GENERIC}
                            dataId={'modal__button--cancel'}
                            onClick={handleCancelButton}
                            ref={cancelButtonRef}
                        >
                            {cancelText || 'Cancel'}
                        </StyledButton>
                    </StyledButtonGroup>
                )}
            </StyledModal>
        );
    },
);
Modal.displayName = 'Modal';

export { Overlay, StyledModal, StyledModalTitleBar, StyledModalContent, StyledButton, StyledButtonGroup };
